import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ToolCard } from '../components/ToolCard';
import { aiTools, freeTools, tools } from '../data/tools';
import { Seo } from '../lib/seo';
import { absoluteUrl } from '../lib/site';

const quickTasks = [
  ['Improve image quality', '/tools/enhance'],
  ['Remove an object', '/tools/object-remover'],
  ['Change a background', '/tools/background-changer'],
  ['Extend an image', '/tools/image-extender'],
  ['Convert image to SVG', '/tools/vectorize'],
  ['Compress for free', '/tools/compress']
] as const;

export function ToolsPage() {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return tools;
    return tools.filter((tool) =>
      [tool.name, tool.description, tool.eyebrow, tool.category ?? '', ...tool.recommendedFor, ...tool.searchTerms]
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [query]);

  const filteredAi = filtered.filter((tool) => tool.mode !== 'free');
  const filteredFree = filtered.filter((tool) => tool.mode === 'free');

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Nexhance AI image tools and free image utilities',
    itemListElement: tools.map((tool, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: tool.name,
      url: absoluteUrl(`/tools/${tool.slug}`)
    }))
  };

  return (
    <section className="page-section tools-page">
      <Seo
        title="AI Image Tools & Free Image Utilities - Nexhance AI"
        description="Use focused AI image tools for enhancement, restoration and editing, plus free browser utilities for compression, resizing, cropping, conversion, colors and metadata."
        path="/tools"
        jsonLd={itemListSchema}
      />
      <div className="shell">
        <div className="page-hero compact-page-hero">
          <p className="eyebrow">Image tools for real workflows</p>
          <h1>Focused AI tools, with free utilities kept clearly separate.</h1>
          <p>Nexhance AI now combines specialist AI workflows for difficult image edits with a separate set of everyday browser utilities that use no credits. Choose the problem first, then use the lightest tool that solves it.</p>
        </div>

        <div className="quick-task-row" aria-label="Popular image tasks">
          {quickTasks.map(([label, to]) => <Link key={to} to={to}>{label}</Link>)}
        </div>

        <label className="tool-search">
          <Search size={19} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search: blur, vector, object, resize, color, background…" />
          <span>{filtered.length} tools</span>
        </label>

        {filtered.length === 0 ? (
          <div className="empty-tool-search">
            <h2>No matching workflow</h2>
            <p>Try a task such as blur, old photo, object removal, background, vector, resize, compress or color.</p>
          </div>
        ) : <>
          {filteredAi.length > 0 && (
            <section className="tool-catalog-section" aria-labelledby="ai-tools-heading">
              <div className="section-heading">
                <p className="eyebrow">AI image studio · {aiTools.length} workflows</p>
                <h2 id="ai-tools-heading">AI tools for enhancement, repair and generative editing</h2>
                <p>These tools use account credits because they call dedicated AI image-processing models. Credit estimates are shown before processing.</p>
              </div>
              <div className="tool-grid full-tool-grid">
                {filteredAi.map((tool, index) => <ToolCard key={tool.slug} tool={tool} index={index} />)}
              </div>
              <div className="tool-description-directory" aria-label="Detailed AI tool descriptions">
                {filteredAi.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <article className="tool-description-card" key={`${tool.slug}-description`}>
                      <div className="tool-description-card-head">
                        <span className="tool-icon"><Icon size={19} /></span>
                        <div><p className="eyebrow">{tool.category ?? 'AI image workflow'}</p><h3><Link to={`/tools/${tool.slug}`}>{tool.name}</Link></h3></div>
                      </div>
                      <p>{tool.longDescription}</p>
                      <div className="tool-description-meta"><span>Popular searches</span>{tool.searchTerms.slice(0, 3).map((term) => <small key={term}>{term}</small>)}</div>
                      <Link className="inline-link" to={`/tools/${tool.slug}`}>Open {tool.shortName.toLowerCase()} <span aria-hidden="true">→</span></Link>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          {filteredFree.length > 0 && (
            <section className="tool-catalog-section free-tools-section" aria-labelledby="free-tools-heading">
              <div className="section-heading">
                <p className="eyebrow">Free image utilities · {freeTools.length} tools</p>
                <h2 id="free-tools-heading">Everyday image tasks with 0 credits</h2>
                <p>Compression, resizing, cropping, conversion, social presets, color sampling, palette extraction and metadata-clean exports run in a supported browser without an AI processing job.</p>
              </div>
              <div className="tool-grid full-tool-grid">
                {filteredFree.map((tool, index) => <ToolCard key={tool.slug} tool={tool} index={index} />)}
              </div>
              <div className="tool-description-directory" aria-label="Detailed free image utility descriptions">
                {filteredFree.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <article className="tool-description-card" key={`${tool.slug}-description`}>
                      <div className="tool-description-card-head">
                        <span className="tool-icon"><Icon size={19} /></span>
                        <div><p className="eyebrow">Free browser utility · 0 credits</p><h3><Link to={`/tools/${tool.slug}`}>{tool.name}</Link></h3></div>
                      </div>
                      <p>{tool.longDescription}</p>
                      <div className="tool-description-meta"><span>Popular searches</span>{tool.searchTerms.slice(0, 3).map((term) => <small key={term}>{term}</small>)}</div>
                      <Link className="inline-link" to={`/tools/${tool.slug}`}>Use {tool.shortName.toLowerCase()} <span aria-hidden="true">→</span></Link>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </>}

        <section className="tool-diagnosis-section" aria-labelledby="tool-diagnosis-heading">
          <div className="tool-diagnosis-heading">
            <div><p className="eyebrow">Choose by visible problem</p><h2 id="tool-diagnosis-heading">Do not spend AI credits on a task a normal utility can handle.</h2></div>
            <p>Use free utilities for deterministic file operations. Use AI when the job requires reconstruction, semantic editing, restoration or generated visual content.</p>
          </div>
          <div className="tool-diagnosis-grid">
            <article><h3>Blurry or noisy</h3><p>Use <Link to="/tools/deblur">AI Deblur</Link> or <Link to="/tools/denoise">AI Denoise</Link> when visible detail needs restoration. A free resizer cannot recover blur.</p><Link className="inline-link" to="/guides/denoise-vs-deblur-vs-sharpen">Compare repair workflows</Link></article>
            <article><h3>Unwanted person or object</h3><p>Use <Link to="/tools/object-remover">AI Object Remover</Link> when the missing area must be reconstructed from the surrounding scene.</p><Link className="inline-link" to="/tools/image-editor">Try a more general AI edit</Link></article>
            <article><h3>Need more canvas</h3><p>Use <Link to="/tools/image-extender">AI Image Extender</Link> to generate new surroundings. Use <Link to="/tools/crop">Free Image Cropper</Link> when you only need to remove edges.</p></article>
            <article><h3>Need a smaller file</h3><p>Use <Link to="/tools/compress">Free Image Compressor</Link>. Compression is a browser utility and should not consume AI credits.</p></article>
            <article><h3>Need exact dimensions</h3><p>Use <Link to="/tools/resize">Free Image Resizer</Link> for normal resizing or <Link to="/tools/upscale">AI Image Upscaler</Link> when a small source needs reconstructed detail.</p></article>
            <article><h3>Need a vector asset</h3><p>Use <Link to="/tools/vectorize">AI Image Vectorizer</Link> for logos, icons and flat artwork that should become scalable SVG output.</p></article>
          </div>
        </section>

        <section className="tool-preflight-section" aria-labelledby="tool-preflight-heading">
          <div className="section-heading">
            <p className="eyebrow">Before you process</p>
            <h2 id="tool-preflight-heading">Four checks prevent most disappointing results.</h2>
          </div>
          <div className="tool-preflight-grid">
            <article><strong>1. Start with the best source</strong><p>Prefer the original camera file, export or scan instead of a screenshot or messaging-app copy when possible.</p></article>
            <article><strong>2. Pick the correct tool class</strong><p>Do not use generative AI for a simple crop, resize, conversion or compression job.</p></article>
            <article><strong>3. Use the smallest useful setting</strong><p>Choose the least aggressive strength, expansion or upscale that reaches the real delivery requirement.</p></article>
            <article><strong>4. Inspect sensitive details</strong><p>Check faces, text, logos, product labels, architecture and repeating patterns after AI editing.</p></article>
          </div>
        </section>

        <div className="tools-seo-content">
          <div><p className="eyebrow">One image platform, two clear tool classes</p><h2>Use AI when pixels need reconstruction. Use free utilities when they do not.</h2></div>
          <div>
            <p><strong>Enhance and repair:</strong> improve general image quality, upscale resolution, restore old photos, recover faces, deblur, denoise, sharpen, relight and improve product imagery.</p>
            <p><strong>Edit with AI:</strong> remove objects or text, change backgrounds, extend the canvas, colorize monochrome photos and make focused prompt-based edits.</p>
            <p><strong>Free utilities:</strong> compress, resize, crop, convert formats, prepare social-media dimensions, sample colors, create palettes and export a metadata-clean image copy without spending credits.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
